import { useState, useEffect, useCallback, type ReactElement } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/Breadcrumb';
import { BillingBadge } from '../../components/BillingBadge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import type {
  Dispatch,
  DispatchStatus,
  CampaignMetrics
} from '../../types/dispatch';
import {
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Eye,
  Send,
  AlertCircle,
  RefreshCw,
  Download,
  Loader2,
  Zap,
  Gift,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from 'lucide-react';

// ============================================
// STATUS CONFIG
// ============================================

const statusBadges: Record<DispatchStatus, { bg: string; text: string; icon: ReactElement; label: string }> = {
  pending: { bg: 'bg-warning/10', text: 'text-warning', icon: <Clock size={13} />, label: 'Pendente' },
  queued: { bg: 'bg-bg-3', text: 'text-text-2', icon: <Clock size={13} />, label: 'Na Fila' },
  processing: { bg: 'bg-info/10', text: 'text-accent-1', icon: <Loader2 size={13} className="animate-spin" />, label: 'Processando' },
  accepted: { bg: 'bg-info/10', text: 'text-accent-1', icon: <CheckCircle size={13} />, label: 'Aceita' },
  sent: { bg: 'bg-info/10', text: 'text-accent-1', icon: <Send size={13} />, label: 'Enviada' },
  delivered: { bg: 'bg-success/10', text: 'text-success', icon: <CheckCircle size={13} />, label: 'Entregue' },
  read: { bg: 'bg-accent-2/10', text: 'text-accent-2', icon: <Eye size={13} />, label: 'Lida' },
  failed: { bg: 'bg-danger/10', text: 'text-danger', icon: <XCircle size={13} />, label: 'Falha' },
  cancelled: { bg: 'bg-bg-3', text: 'text-text-2', icon: <AlertCircle size={13} />, label: 'Cancelada' },
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function CampaignDetails() {
  const { id: wabaAccountId, campaignId } = useParams<{ id: string; campaignId: string }>();
  const { accessToken } = useAuth();
  const location = useLocation();
  const state = location.state as { templateId?: string; templateName?: string } | null;

  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<DispatchStatus | 'all'>('all');
  const [searchPhone, setSearchPhone] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDispatches, setTotalDispatches] = useState(0);
  const limit = 50;

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchMetrics = useCallback(async () => {
    if (!accessToken || !campaignId) return;
    try {
      const data = await apiClient.getCampaignMetrics(campaignId, accessToken);
      setMetrics(data);
    } catch (err: any) {
      setError(`Erro ao carregar métricas: ${err.message}`);
    }
  }, [accessToken, campaignId]);

  const fetchDispatches = useCallback(async (isRefresh = false) => {
    if (!accessToken || !campaignId) return;

    if (!isRefresh) setLoading(true);

    try {
      const data = await apiClient.getDispatches(
        {
          campaignId,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          recipientPhone: searchPhone || undefined,
          limit,
          offset: (currentPage - 1) * limit,
        },
        accessToken
      );
      setDispatches(data.data);
      setTotalDispatches(data.total);
      if (!isRefresh) setError(null);
    } catch (err: any) {
      setError(`Erro ao carregar disparos: ${err.message}`);
      setDispatches([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [accessToken, campaignId, statusFilter, searchPhone, currentPage]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);
  useEffect(() => { fetchDispatches(); }, [fetchDispatches]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMetrics();
    fetchDispatches(true);
  };

  const handleExportExcel = async () => {
    if (!accessToken || !campaignId) return;
    setExporting(true);
    try {
      const blob = await apiClient.exportCampaignDispatches(campaignId, accessToken, statusFilter);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `disparos_${metrics?.campaignName || 'campanha'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`Erro ao exportar: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const formatNumber = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

  const formatDispatchError = (error: string | null): string | null => {
    if (!error) return null;
    const translateError = (errorMsg: string): string => {
      if (errorMsg.includes('Media upload error')) {
        if (errorMsg.includes('403') || errorMsg.includes('Forbidden'))
          return 'Erro de mídia: Acesso negado ao arquivo (403). Verifique se a URL é pública.';
        if (errorMsg.includes('404') || errorMsg.includes('Not Found'))
          return 'Erro de mídia: Arquivo não encontrado (404).';
        if (errorMsg.includes('timeout') || errorMsg.includes('Timeout'))
          return 'Erro de mídia: Tempo esgotado ao baixar o arquivo.';
        return 'Erro de mídia: Falha ao carregar o arquivo.';
      }
      if (errorMsg.includes('Variáveis insuficientes') || errorMsg.includes('Variáveis vazias') || errorMsg.includes('Valor inválido'))
        return errorMsg;
      if (errorMsg.includes('recipient') && errorMsg.includes('invalid'))
        return 'Número de telefone inválido';
      if (errorMsg.includes('not a valid WhatsApp'))
        return 'Número não possui WhatsApp';
      if (errorMsg.includes('rate limit') || errorMsg.includes('too many'))
        return 'Limite de envio atingido.';
      return errorMsg;
    };
    try {
      const parsed = JSON.parse(error);
      if (parsed.message === '' && parsed.details === '') return 'Erro desconhecido';
      const parts: string[] = [];
      if (parsed.title) parts.push(parsed.title);
      if (parsed.message && parsed.message !== parsed.title) parts.push(parsed.message);
      if (parsed.details) parts.push(parsed.details);
      if (parts.length > 0) return translateError(parts.join(': '));
      return error;
    } catch {
      return translateError(error);
    }
  };

  // Calcula mensagens que de fato passaram pelo envio (geraram custo potencial)
  // sent + delivered + read = mensagens que chegaram a ser enviadas
  const effectivelySent = metrics
    ? metrics.byStatus.sent + metrics.byStatus.delivered + metrics.byStatus.read
    : 0;

  const totalPages = Math.ceil(totalDispatches / limit);

  // ============================================
  // RENDER
  // ============================================

  if (loading && !metrics && dispatches.length === 0) {
    return <LoadingSpinner fullPage text="Carregando detalhes da campanha..." />;
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={
            state?.templateId
              ? [
                  { label: 'Ambiente WABA', path: `/waba/${wabaAccountId}/environment` },
                  {
                    label: state.templateName || 'Histórico de Execuções',
                    path: `/waba/${wabaAccountId}/templates/${state.templateId}/executions`
                  },
                  { label: metrics?.campaignName || 'Detalhes da Campanha' },
                ]
              : [
                  { label: 'Ambiente WABA', path: `/waba/${wabaAccountId}/environment` },
                  { label: metrics?.campaignName || 'Detalhes da Campanha' },
                ]
          }
        />

        {/* ======== HEADER ======== */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-1">
              {metrics?.campaignName || 'Detalhes da Campanha'}
            </h1>
            {metrics?.templateCategory && (
              <p className="text-sm text-text-2 mt-1">
                Categoria: <span className="font-medium text-text-1">{metrics.templateCategory}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 text-sm text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg border border-border-1 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger/10 border border-danger/30 rounded-xl flex items-start gap-3">
            <AlertCircle className="text-danger flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-danger text-sm font-medium">{error}</p>
              <button
                onClick={handleRefresh}
                className="mt-2 text-sm text-danger font-medium hover:underline"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {/* ======== FUNIL DE ENVIO + CUSTOS ======== */}
        {metrics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">

            {/* Funil de Envio — barras paralelas progressivas */}
            <div className="lg:col-span-2 bg-bg-2 rounded-xl border border-border-1 p-5">
              <h3 className="text-sm font-semibold text-text-1 mb-5">Funil de Envio</h3>

              <div className="space-y-5">
                {/* TOTAL */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-text-3 uppercase tracking-wider w-20">Total</span>
                      <span className="text-2xl font-bold text-text-1">{formatNumber(metrics.totalDispatches)}</span>
                    </div>
                    {(metrics.byStatus.queued + metrics.byStatus.processing + metrics.byStatus.accepted) > 0 && (
                      <span className="text-xs text-text-3">
                        {formatNumber(metrics.byStatus.queued + metrics.byStatus.processing + metrics.byStatus.accepted)} em processamento
                      </span>
                    )}
                  </div>
                  <div className="h-3 rounded-full bg-bg-3 overflow-hidden">
                    <div className="h-full bg-text-2/40 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>

                {/* ENVIADOS */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-accent-1 uppercase tracking-wider w-20">Enviados</span>
                      <span className="text-2xl font-bold text-accent-1">{formatNumber(effectivelySent)}</span>
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-bg-3 overflow-hidden">
                    <div
                      className="h-full bg-accent-1 rounded-full transition-all"
                      style={{ width: `${metrics.totalDispatches > 0 ? (effectivelySent / metrics.totalDispatches) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-3 mt-1">
                    {metrics.totalDispatches > 0 ? ((effectivelySent / metrics.totalDispatches) * 100).toFixed(1) : '0'}% do total
                  </p>
                </div>

                {/* ENTREGUES */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-success uppercase tracking-wider w-20">Entregues</span>
                      <span className="text-2xl font-bold text-success">
                        {formatNumber(metrics.byStatus.delivered + metrics.byStatus.read)}
                      </span>
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-bg-3 overflow-hidden">
                    <div
                      className="h-full bg-success rounded-full transition-all"
                      style={{ width: `${metrics.totalDispatches > 0 ? ((metrics.byStatus.delivered + metrics.byStatus.read) / metrics.totalDispatches) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-3 mt-1">
                    {effectivelySent > 0 ? (((metrics.byStatus.delivered + metrics.byStatus.read) / effectivelySent) * 100).toFixed(1) : '0'}% dos enviados
                  </p>
                </div>

                {/* LIDOS */}
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-accent-2 uppercase tracking-wider w-20">Lidos</span>
                      <span className="text-2xl font-bold text-accent-2">{formatNumber(metrics.byStatus.read)}</span>
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-bg-3 overflow-hidden">
                    <div
                      className="h-full bg-accent-2 rounded-full transition-all"
                      style={{ width: `${metrics.totalDispatches > 0 ? (metrics.byStatus.read / metrics.totalDispatches) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-3 mt-1">
                    {effectivelySent > 0 ? ((metrics.byStatus.read / effectivelySent) * 100).toFixed(1) : '0'}% dos enviados
                  </p>
                </div>

                {/* FALHAS */}
                {(metrics.byStatus.failed + metrics.byStatus.cancelled) > 0 && (
                  <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-danger uppercase tracking-wider w-20">Falhas</span>
                        <span className="text-2xl font-bold text-danger">
                          {formatNumber(metrics.byStatus.failed + metrics.byStatus.cancelled)}
                        </span>
                      </div>
                    </div>
                    <div className="h-3 rounded-full bg-bg-3 overflow-hidden">
                      <div
                        className="h-full bg-danger rounded-full transition-all"
                        style={{ width: `${metrics.totalDispatches > 0 ? ((metrics.byStatus.failed + metrics.byStatus.cancelled) / metrics.totalDispatches) * 100 : 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-text-3 mt-1">
                      {metrics.totalDispatches > 0 ? (((metrics.byStatus.failed + metrics.byStatus.cancelled) / metrics.totalDispatches) * 100).toFixed(1) : '0'}% do total
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Custo & Cobrança */}
            <div className="bg-bg-2 rounded-xl border border-border-1 p-5">
              {/* Custo Total */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg bg-warning/10">
                    <DollarSign size={14} className="text-warning" />
                  </div>
                  <span className="text-xs text-text-3 uppercase tracking-wider font-semibold">Custo Total</span>
                </div>
                <p className="text-2xl font-bold text-text-1">{formatCurrency(metrics.totalCost)}</p>
                {metrics.totalCost && effectivelySent > 0 && (
                  <p className="text-xs text-text-3 mt-1">
                    Média: {formatCurrency(metrics.totalCost / effectivelySent)} / msg enviada
                  </p>
                )}
              </div>

              <div className="border-t border-border-1 pt-4">
                <h3 className="text-sm font-semibold text-text-1 mb-3">Resumo de Cobrança</h3>

                <div className="space-y-2.5">
                  {/* Mensagens com custo */}
                  <div className="flex items-center justify-between p-2.5 bg-accent-1/5 rounded-lg border border-accent-1/10">
                    <div className="flex items-center gap-2">
                      <Zap size={13} className="text-accent-1" />
                      <span className="text-xs text-text-2">Geraram custo</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-text-1">
                        {formatNumber(metrics.billableMessages ?? effectivelySent)}
                      </p>
                    </div>
                  </div>

                  {/* Mensagens grátis */}
                  <div className="flex items-center justify-between p-2.5 bg-bg-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Gift size={13} className="text-success" />
                      <span className="text-xs text-text-2">Grátis</span>
                    </div>
                    <p className="text-sm font-bold text-text-1">
                      {formatNumber(metrics.freeMessages ?? 0)}
                    </p>
                  </div>

                  {/* Divergências de categoria */}
                  {(metrics.categoryMismatches ?? 0) > 0 && (
                    <div className="flex items-center justify-between p-2.5 bg-warning/5 rounded-lg border border-warning/10">
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={13} className="text-warning" />
                        <span className="text-xs text-text-2">Divergências</span>
                      </div>
                      <p className="text-sm font-bold text-warning">
                        {formatNumber(metrics.categoryMismatches!)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Performance */}
              <div className="border-t border-border-1 mt-4 pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-3">Tempo entrega</span>
                  <span className="text-xs font-medium text-text-1">
                    {metrics.averageDeliveryTime ? `${(metrics.averageDeliveryTime / 1000).toFixed(1)}s` : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-3">Tempo leitura</span>
                  <span className="text-xs font-medium text-text-1">
                    {metrics.averageReadTime ? `${(metrics.averageReadTime / 1000).toFixed(1)}s` : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-3">Taxa de falha</span>
                  <span className={`text-xs font-medium ${metrics.failureRate > 5 ? 'text-danger' : 'text-success'}`}>
                    {metrics.failureRate.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ======== TABELA DE DESTINATÁRIOS ======== */}
        <div className="bg-bg-2 rounded-xl border border-border-1">
          {/* Table Header with Filters */}
          <div className="p-4 border-b border-border-1">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-text-1">
                Destinatários
                <span className="ml-2 text-xs font-normal text-text-3">
                  ({formatNumber(totalDispatches)} total)
                </span>
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {/* Status Filter */}
                <div className="relative">
                  <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-3" />
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value as DispatchStatus | 'all'); setCurrentPage(1); }}
                    className="pl-8 pr-3 py-2 bg-bg-3 border border-border-1 rounded-lg text-sm text-text-1 focus:outline-none focus:border-accent-1 appearance-none cursor-pointer"
                  >
                    <option value="all">Todos</option>
                    <option value="queued">Na Fila</option>
                    <option value="processing">Processando</option>
                    <option value="accepted">Aceitas</option>
                    <option value="sent">Enviadas</option>
                    <option value="delivered">Entregues</option>
                    <option value="read">Lidas</option>
                    <option value="failed">Falhas</option>
                    <option value="cancelled">Canceladas</option>
                  </select>
                </div>

                {/* Phone Search */}
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-3" />
                  <input
                    type="text"
                    placeholder="Buscar telefone..."
                    value={searchPhone}
                    onChange={(e) => { setSearchPhone(e.target.value); setCurrentPage(1); }}
                    className="pl-8 pr-3 py-2 bg-bg-3 border border-border-1 rounded-lg text-sm text-text-1 focus:outline-none focus:border-accent-1 w-44"
                  />
                </div>

                {/* Export Button */}
                <button
                  onClick={handleExportExcel}
                  disabled={exporting || loading}
                  className="flex items-center gap-1.5 px-3 py-2 bg-success text-white rounded-lg hover:bg-success/90 text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {exporting ? (
                    <><Loader2 size={14} className="animate-spin" /> Exportando...</>
                  ) : (
                    <><Download size={14} /> Exportar</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Table Content */}
          {loading ? (
            <div className="py-16">
              <LoadingSpinner text="Carregando destinatários..." />
            </div>
          ) : dispatches.length === 0 ? (
            <div className="py-16 text-center">
              <AlertCircle size={40} className="mx-auto text-text-3 mb-3" />
              <p className="text-sm text-text-2">Nenhum destinatário encontrado</p>
              {statusFilter !== 'all' && (
                <button
                  onClick={() => { setStatusFilter('all'); setSearchPhone(''); setCurrentPage(1); }}
                  className="mt-2 text-xs text-accent-1 hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-border-1">
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Destinatário
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Cobrança
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Erro
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Tentativas
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-text-3 uppercase tracking-wider">
                        Timeline
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-1">
                    {dispatches.map((dispatch) => {
                      const badge = statusBadges[dispatch.status];
                      return (
                        <tr key={dispatch.id} className="hover:bg-bg-3/50 transition-colors">
                          {/* Destinatário */}
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-text-1">
                              {dispatch.recipientName || 'Sem nome'}
                            </p>
                            <p className="text-xs text-text-3">{dispatch.recipientPhone}</p>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                              {badge.icon}
                              {badge.label}
                            </span>
                          </td>

                          {/* Cobrança (unified: badge + cost) */}
                          <td className="px-4 py-3">
                            <BillingBadge
                              isBillable={dispatch.isBillable}
                              billingType={dispatch.billingType}
                              categoryMismatch={dispatch.categoryMismatch}
                              actualCategory={dispatch.actualCategory}
                              expectedCategory={metrics?.templateCategory}
                              cost={dispatch.cost}
                              status={dispatch.status}
                            />
                          </td>

                          {/* Erro */}
                          <td className="px-4 py-3">
                            {dispatch.error ? (
                              <p className="text-xs text-danger max-w-[260px]" title={formatDispatchError(dispatch.error) || ''}>
                                {formatDispatchError(dispatch.error)}
                              </p>
                            ) : (
                              <span className="text-xs text-text-3">—</span>
                            )}
                          </td>

                          {/* Tentativas */}
                          <td className="px-4 py-3">
                            <span className="text-sm text-text-1">
                              {dispatch.retryCount} / {dispatch.maxRetries}
                            </span>
                          </td>

                          {/* Timeline */}
                          <td className="px-4 py-3">
                            <div className="text-xs text-text-3 space-y-0.5">
                              {dispatch.deliveredAt && (
                                <p><span className="text-success">Entregue:</span> {formatDateTime(dispatch.deliveredAt)}</p>
                              )}
                              {dispatch.readAt && (
                                <p><span className="text-accent-2">Lido:</span> {formatDateTime(dispatch.readAt)}</p>
                              )}
                              {dispatch.failedAt && (
                                <p><span className="text-danger">Falhou:</span> {formatDateTime(dispatch.failedAt)}</p>
                              )}
                              {!dispatch.deliveredAt && !dispatch.readAt && !dispatch.failedAt && (
                                <p><span className="text-text-3">Criado:</span> {formatDateTime(dispatch.createdAt)}</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border-1">
                  <p className="text-xs text-text-3">
                    {formatNumber((currentPage - 1) * limit + 1)}–{formatNumber(Math.min(currentPage * limit, totalDispatches))} de {formatNumber(totalDispatches)}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-text-2 hover:text-text-1 hover:bg-bg-3 border border-border-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={14} />
                      Anterior
                    </button>

                    <span className="px-3 py-1.5 text-xs text-text-2">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-text-2 hover:text-text-1 hover:bg-bg-3 border border-border-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Próxima
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CampaignDetails;
